import { StandardAccessRight, UserOverview } from "api/generated/model";

type ResourceType =
    | "ControlEntity"
    | "RiskEntity"
    | "UserGroup"
    | "RiskDirectory"
    | "SavedReport"
    | "Classification"
    | "ControlProgram"
    | "TestingProgram";

interface TypedAccessRight<
    RT extends ResourceType,
    RG extends readonly StandardAccessRight[],
> {
    readonly type: RT;
    readonly accessRights: RG;
    readonly requiresAdmin: boolean;
    readonly defaultAccessRight: RG[number];
}

type AccessRightsVariants<RAR extends ResourceAccessRights> =
    RAR["accessRights"][number];

type AllRights = Readonly<["Admin", "Edit", "View"]>;
const AllRightsInstance = ["Admin", "Edit", "View"] as const;

export type ClassificationAccessRights = TypedAccessRight<
    "Classification",
    AllRights
>;
export type ControlProgramAccessRights = TypedAccessRight<
    "ControlProgram",
    AllRights
>;
export type TestingProgramAccessRights = TypedAccessRight<
    "TestingProgram",
    AllRights
>;
export type ControlEntityAccessRights = TypedAccessRight<
    "ControlEntity",
    AllRights
>;
export type RiskEntityAccessRights = TypedAccessRight<"RiskEntity", AllRights>;
export type RiskDirectoryAccessRights = TypedAccessRight<
    "RiskDirectory",
    AllRights
>;
export type SavedReportAccessRights = TypedAccessRight<
    "SavedReport",
    AllRights
>;
export type UserGroupAccessRights = TypedAccessRight<
    "UserGroup",
    Readonly<["Admin", "Edit"]>
>;

type ResourceAccessRights =
    | ControlEntityAccessRights
    | RiskEntityAccessRights
    | UserGroupAccessRights
    | RiskDirectoryAccessRights
    | SavedReportAccessRights
    | ClassificationAccessRights
    | ControlProgramAccessRights
    | TestingProgramAccessRights;

const ClassificationAccessRightsInstance: ClassificationAccessRights = {
    type: "Classification",
    accessRights: AllRightsInstance,
    requiresAdmin: true,
    defaultAccessRight: "View",
} as const;

const ControlProgramAccessRightsInstance: ControlProgramAccessRights = {
    type: "ControlProgram",
    accessRights: AllRightsInstance,
    requiresAdmin: true,
    defaultAccessRight: "View",
} as const;

const TestingProgramAccessRightsInstance: TestingProgramAccessRights = {
    type: "TestingProgram",
    accessRights: AllRightsInstance,
    requiresAdmin: true,
    defaultAccessRight: "View",
} as const;

const ControlEntityAccessRightsInstance: ControlEntityAccessRights = {
    type: "ControlEntity",
    accessRights: AllRightsInstance,
    requiresAdmin: true,
    defaultAccessRight: "View",
} as const;

const RiskEntityAccessRightsInstance: RiskEntityAccessRights = {
    type: "RiskEntity",
    accessRights: AllRightsInstance,
    requiresAdmin: true,
    defaultAccessRight: "View",
} as const;

const RiskDirectoryAccessRightsInstance: RiskDirectoryAccessRights = {
    type: "RiskDirectory",
    accessRights: AllRightsInstance,
    requiresAdmin: true,
    defaultAccessRight: "View",
} as const;

const SavedReportAccessRightsInstance: SavedReportAccessRights = {
    type: "SavedReport",
    accessRights: AllRightsInstance,
    requiresAdmin: true,
    defaultAccessRight: "View",
} as const;

const UserGroupAccessRightsInstance: UserGroupAccessRights = {
    type: "UserGroup",
    accessRights: ["Admin", "Edit"],
    requiresAdmin: false,
    defaultAccessRight: "Edit",
} as const;

interface UserAndRights<RAR extends ResourceAccessRights> {
    user: UserOverview;
    accessRight: AccessRightsVariants<RAR>;
}

export type {
    UserAndRights,
    ResourceAccessRights,
    TypedAccessRight,
    AllRights,
    AccessRightsVariants,
};
export {
    ClassificationAccessRightsInstance,
    ControlProgramAccessRightsInstance,
    ControlEntityAccessRightsInstance,
    RiskEntityAccessRightsInstance,
    RiskDirectoryAccessRightsInstance,
    SavedReportAccessRightsInstance,
    UserGroupAccessRightsInstance,
    TestingProgramAccessRightsInstance,
};
